/** Read-only history dialog for the satisfaction column. */

import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
} from "@/shared/components/ui/dialog";
import { cn } from "@/shared/lib/utils";
import {
	SATISFACTION_LABELS,
	satisfactionClass,
	useSatisfactionHistory,
	type SatisfactionEntry,
	type SatisfactionScore,
} from "../useSatisfaction";

function formatDateTime(iso: string): string {
	const d = new Date(iso);
	if (Number.isNaN(d.getTime())) return iso;
	return d.toLocaleString(undefined, {
		year: "numeric",
		month: "short",
		day: "numeric",
		hour: "numeric",
		minute: "2-digit",
	});
}

interface HistoryDialogProps {
	profId: string | null;
	profName: string;
	onClose: () => void;
}

export function SatisfactionHistoryDialog({
	profId,
	profName,
	onClose,
}: HistoryDialogProps) {
	// The API already returns newest-first; no client-side sort to drift from it.
	const { data, isLoading, error } = useSatisfactionHistory(profId);

	return (
		<Dialog open={!!profId} onOpenChange={(open) => !open && onClose()}>
			<DialogContent className="max-w-md">
				<DialogHeader>
					<DialogTitle className="text-base">
						Satisfaction history — {profName}
					</DialogTitle>
				</DialogHeader>

				{isLoading && (
					<p className="py-6 text-center text-sm text-muted-foreground">
						Loading…
					</p>
				)}
				{error && (
					<p className="py-6 text-center text-sm text-destructive">
						Could not load history.
					</p>
				)}
				{!isLoading && !error && (data?.length ?? 0) === 0 && (
					<p className="py-6 text-center text-sm text-muted-foreground">
						No scores recorded yet.
					</p>
				)}

				{!isLoading && !error && (data?.length ?? 0) > 0 && (
					<ul className="max-h-[50vh] space-y-2 overflow-y-auto">
						{data?.map((entry: SatisfactionEntry) => (
							<li
								key={entry.id}
								className="flex items-start gap-3 rounded border p-2.5"
							>
								<span
									className={cn(
										"mt-0.5 rounded px-1.5 py-0.5 text-sm font-semibold tabular-nums",
										satisfactionClass(entry.score),
									)}
								>
									{entry.score ?? "—"}
								</span>
								<div className="min-w-0 flex-1">
									<div className="text-xs font-medium">
										{entry.score === null
											? "Note only"
											: (SATISFACTION_LABELS[entry.score as SatisfactionScore] ??
												`Score ${entry.score}`)}
									</div>
									<div className="text-[11px] text-muted-foreground">
										{formatDateTime(entry.createdAt)}
									</div>
									{entry.note && (
										<p className="mt-1 whitespace-pre-wrap text-xs">
											{entry.note}
										</p>
									)}
								</div>
							</li>
						))}
					</ul>
				)}
			</DialogContent>
		</Dialog>
	);
}
